import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, TextField, Button, Paper, Alert, Link as MuiLink } from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { login, resendVerification } from '../api/client';

const LoginPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState('');
    const [loading, setLoading] = useState(false);
    const [showResend, setShowResend] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);

    useEffect(() => {
        if (location.state?.message) {
            setSuccessMsg(location.state.message);
        }
    }, [location.state]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleResend = async () => {
        setResendLoading(true);
        setError(null);
        try {
            const res = await resendVerification(formData.email);
            setSuccessMsg(res.message);
            setShowResend(false);
        } catch (err) {
            setError(err.message);
        } finally {
            setResendLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccessMsg(''); // Clear previous successes
        setShowResend(false);
        setLoading(true);
        try {
            const data = await login(formData);
            // Store token
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));
            // Redirect
            const pendingBooking = localStorage.getItem('pendingBookingEventId');
            if (pendingBooking) {
                navigate('/schedule');
            } else {
                navigate('/');
            }
        } catch (err) {
            setError(err.message);
            if (err.message && err.message.includes("Email not verified")) {
                setShowResend(true);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ py: 8, bgcolor: '#f4f6f8', minHeight: '80vh', display: 'flex', alignItems: 'center' }}>
            <Container maxWidth="xs">
                <Paper elevation={3} sx={{ p: 4, borderRadius: 4 }}>
                    <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 800, color: 'primary.main' }}>
                        Welcome Back
                    </Typography>

                    {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                    {showResend && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            fullWidth
                            onClick={handleResend}
                            disabled={resendLoading}
                            sx={{ mb: 3 }}
                        >
                            {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                        </Button>
                    )}

                    {successMsg && <Alert severity="success" sx={{ mb: 3 }}>{successMsg}</Alert>}

                    <form onSubmit={handleSubmit}>
                        <TextField
                            fullWidth
                            label="Email Address"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            margin="normal"
                            variant="outlined"
                        />
                        <TextField
                            fullWidth
                            label="Password"
                            name="password"
                            type="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            margin="normal"
                            variant="outlined"
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <MuiLink component={Link} to="/forgot-password" variant="body2">
                                Forgot Password?
                            </MuiLink>
                        </Box>
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            disabled={loading}
                            sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}
                        >
                            {loading ? 'Logging In...' : 'Log In'}
                        </Button>
                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <Typography variant="body2">
                                New here?{' '}
                                <MuiLink component={Link} to="/register" fontWeight="bold">
                                    Create an Account
                                </MuiLink>
                            </Typography>
                        </Box>
                    </form>
                </Paper>
            </Container>
        </Box>
    );
};

export default LoginPage;
